import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink, Check, Award, Globe, Zap } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import BackButton from '../components/ui/BackButton';
import { Breadcrumbs } from '../components/website/Breadcrumbs';

// Partner data repository
// relationship: 'dealer' (authorized dealer), 'partner' (confirmed partnership), 'integration' (confirmed control integration), 'brand' (featured brand, not a confirmed partnership)
const RELATIONSHIP_COPY = {
  dealer: { badge: 'Authorized Dealer', heading: (n) => `Apollo Is an Authorized ${n} Dealer`, text: (n) => `Apollo Shades is an authorized ${n} dealer. We specify, supply and install ${n} systems for homes and businesses across New York, with compatibility confirmed for each project.` },
  partner: { badge: 'Partner', heading: (n) => `Why We Partner with ${n}`, text: (n) => `We work with ${n} to bring its systems to our customers, with product availability and compatibility confirmed for each project.` },
  integration: { badge: 'Integration', heading: (n) => `Why We Integrate with ${n}`, text: (n) => `Apollo motorized shades integrate with ${n}, so shades can be controlled alongside the rest of your home or building. Compatibility is confirmed for each project.` },
  brand: { badge: 'Featured Brand', heading: (n) => `About ${n}`, text: (n) => `An overview of ${n} and its products. Availability of ${n} products through Apollo is confirmed for each project.` },
};

const PARTNER_DATA = {
  'coulisse': {
    relationship: 'partner',
    name: 'Coulisse',
    tagline: 'Dutch Design & Motion Innovation',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/lzxjiv6m_image.png',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/2c543b3370d04e876fbf8e8f8824ab03edabb2217cfcef630354bb129509aab3.jpeg',
    description: 'Coulisse is a Dutch window covering company known for contemporary design, curated shade fabrics and its Motionblinds motor platform. Apollo builds custom roller, Roman and zebra shades with Coulisse components and fabrics, and uses Motionblinds motors to bring quiet, app- and voice-controlled motorization to homes and businesses across New York.',
    website: 'https://coulisse.com',
    features: [
      'Contemporary shade fabric collections in light-filtering, screen and blackout opacities',
      'Motionblinds motors for roller, Roman, zebra and drapery systems',
      'Battery and hardwired motor options with quiet operation',
      'Works with Apple Home, Google Home, Alexa and Matter',
      'Precision-engineered roller and Roman shade components',
      'Design-led systems suited to modern residential and commercial interiors'
    ],
    products: [
      { name: 'Motionblinds Motors', description: 'Battery and hardwired shade motors' },
      { name: 'Motionblinds Bridge & App', description: 'Smart control, scenes and schedules' },
      { name: 'Shade Fabrics', description: 'Curated textures and opacities' },
      { name: 'Shade Systems', description: 'Roller, Roman and zebra components' }
    ],
    stats: [
      { value: 'NL', label: 'Dutch Design' },
      { value: '4', label: 'Smart Platforms' },
      { value: 'Matter', label: 'Ready' }
    ]
  },
  'somfy': {
    relationship: 'dealer',
    name: 'Somfy',
    tagline: 'World Leader in Motorization',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/5hn0d907_image.png',
    heroImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&h=800&fit=crop',
    description: 'Since 1969, Somfy has been pioneering motorization and automation solutions for window coverings. As the world leader in automatic controls for openings and closures, Somfy develops solutions that improve comfort, security, and energy efficiency in buildings.',
    website: 'https://somfy.com',
    features: [
      'Industry-leading motor technology with ultra-quiet operation',
      'Comprehensive smart home integration (Alexa, Google, HomeKit)',
      'Extensive range from residential to commercial solutions',
      'RTS and io-homecontrol wireless protocols',
      'Solar-powered options for sustainable automation',
      'TaHoma smart hub for unified control'
    ],
    products: [
      { name: 'Sonesse Motors', description: 'Ultra-quiet motors for interior shades' },
      { name: 'Glydea Motors', description: 'Premium motorized drapery tracks' },
      { name: 'TaHoma Hub', description: 'Smart home control center' },
      { name: 'Connexoon', description: 'Simplified smart control' }
    ],
    stats: [
      { value: '50+', label: 'Years of Innovation' },
      { value: '400M+', label: 'Motors Installed' },
      { value: '58', label: 'Countries' }
    ]
  },
  'rollease-acmeda': {
    relationship: 'brand',
    name: 'Rollease Acmeda',
    tagline: 'Innovation in Window Coverings',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/ihjcblfi_image.png',
    heroImage: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&h=800&fit=crop',
    description: 'Rollease Acmeda is a global manufacturer of window covering components, motors, and automation systems. With manufacturing facilities across three continents, they deliver innovative solutions for both residential and commercial applications.',
    website: 'https://rolleaseacmeda.com',
    features: [
      'Automate motors with breakthrough battery and hardwired options',
      'Patented Pulse 2 hub for smart home integration',
      'Commercial-grade systems for large installations',
      'Extensive component catalog for fabricators',
      'Chain and motorized clutch systems',
      'Custom solutions for unique applications'
    ],
    products: [
      { name: 'Automate Pulse 2', description: 'Smart hub with voice control' },
      { name: 'Automate Motors', description: 'Battery and hardwired options' },
      { name: 'Roller Components', description: 'Tubes, brackets, and clutches' },
      { name: 'Commercial Systems', description: 'Large-scale automation' }
    ],
    stats: [
      { value: '3', label: 'Continents' },
      { value: '100+', label: 'Countries Served' },
      { value: '1000s', label: 'Products' }
    ]
  },
  'vertilux': {
    relationship: 'brand',
    name: 'Vertilux',
    tagline: 'Components & Fabrics Excellence',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/bcx7r6tw_image.png',
    heroImage: 'https://images.unsplash.com/photo-1497366412874-3415097a27e7?w=1920&h=800&fit=crop',
    description: 'For over 40 years, Vertilux has been a leading supplier of window treatment components and fabrics. Their comprehensive product line includes everything from hardware to premium shade fabrics, serving fabricators and dealers worldwide.',
    website: 'https://vertilux.com',
    features: [
      'Extensive fabric collection with performance options',
      'Complete hardware systems for all shade types',
      'Motorization solutions for modern automation',
      'Custom color matching and private labeling',
      'Rapid shipping from multiple distribution centers',
      'Technical support and training programs'
    ],
    products: [
      { name: 'Shade Fabrics', description: 'Screen, blackout, and decorative' },
      { name: 'Cassette Systems', description: 'Designer headrail options' },
      { name: 'Motors', description: 'Tubular motor solutions' },
      { name: 'Components', description: 'Brackets, tubes, and mechanisms' }
    ],
    stats: [
      { value: '40+', label: 'Years Experience' },
      { value: '500+', label: 'Fabric Options' },
      { value: '24hr', label: 'Quick Ship' }
    ]
  },
  'lutron': {
    relationship: 'dealer',
    name: 'Lutron',
    tagline: 'The Light Control Experts',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/ylx0vipe_image.png',
    heroImage: 'https://images.unsplash.com/photo-1704040686510-b747ff423ebb?w=1920&h=800&fit=crop&q=80',
    description: 'Lutron pioneered the dimmer switch in 1961 and has since become the leader in lighting and shading control. Their systems are found in homes, offices, and iconic buildings worldwide, known for reliability and elegant design.',
    website: 'https://lutron.com',
    features: [
      'Serena shades with Apple HomeKit built-in',
      'Triathlon battery-powered motors',
      'Caseta and RadioRA smart home systems',
      'Precision-engineered for whisper-quiet operation',
      'Palladiom and Sivoia QS for luxury installations',
      'Manufacturer-backed motor warranty'
    ],
    products: [
      { name: 'Serena Shades', description: 'Smart shades with HomeKit' },
      { name: 'Triathlon', description: 'Battery-powered precision' },
      { name: 'Sivoia QS', description: 'Commercial & luxury systems' },
      { name: 'Palladiom', description: 'Designer drapery tracks' }
    ],
    stats: [
      { value: '60+', label: 'Years Innovation' },
      { value: '#1', label: 'In Light Control' },
      { value: 'NYC', label: 'Installed by Apollo' }
    ]
  },
  'hunter-douglas': {
    relationship: 'brand',
    name: 'Hunter Douglas',
    tagline: 'World Leader in Window Coverings',
    logo: null,
    heroImage: 'https://images.unsplash.com/photo-1560448204-603b3fc33ddc?w=1920&h=800&fit=crop',
    description: 'Hunter Douglas is the world\'s leading manufacturer of custom window coverings, architectural products, and building materials. Their iconic brands like Silhouette, Duette, and Pirouette represent the pinnacle of window treatment design.',
    website: 'https://hunterdouglas.com',
    features: [
      'Iconic product designs like Silhouette and Pirouette',
      'PowerView motorization with smart home integration',
      'Duette honeycomb shades for energy efficiency',
      'Alustra collection for luxury interiors',
      'Commercial solutions for architects and designers',
      'Warranty terms set by the manufacturer for each product'
    ],
    products: [
      { name: 'Silhouette', description: 'Signature S-vane design' },
      { name: 'Duette', description: 'Energy-efficient honeycomb' },
      { name: 'Pirouette', description: 'Soft fabric vanes' },
      { name: 'PowerView', description: 'Smart motorization' }
    ],
    stats: [
      { value: '#1', label: 'Globally' },
      { value: '100+', label: 'Countries' },
      { value: 'NYC', label: 'Availability Confirmed per Project' }
    ]
  },
  'fabricut': {
    relationship: 'brand',
    name: 'Fabricut',
    tagline: 'Premier Fabric House',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/19oc58sp_image.png',
    heroImage: '/img/catalog/fabricut-hero.jpg',
    description: 'Fabricut is a premier fabric house offering extensive collections of designer fabrics, trims, and drapery hardware. With brands like Stroheim, S. Harris, and Vervain, they serve discerning designers and homeowners seeking luxury textiles.',
    website: 'https://fabricut.com',
    features: [
      'Extensive designer fabric collections',
      'Multiple brands: Stroheim, S. Harris, Vervain, Trend',
      'Performance fabrics for high-use applications',
      'Custom drapery hardware and accessories',
      'Sampling and memo programs for designers',
      'Dedicated designer support team'
    ],
    products: [
      { name: 'Drapery Fabrics', description: 'Silk, linen, cotton, and blends' },
      { name: 'Performance', description: 'Stain-resistant options' },
      { name: 'Trims', description: 'Designer embellishments' },
      { name: 'Hardware', description: 'Poles, rings, and finials' }
    ],
    stats: [
      { value: '50K+', label: 'SKUs Available' },
      { value: '4', label: 'Luxury Brands' },
      { value: '75+', label: 'Years Experience' }
    ]
  },
  'control4': {
    relationship: 'integration',
    name: 'Control4',
    tagline: 'Smart Home Automation',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/wr9zqqt9_C4_Logo_FC.png',
    heroImage: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=1920&h=800&fit=crop',
    description: 'Control4 is a leading provider of smart home automation, delivering personalized control of lighting, music, video, climate, and security. Their systems integrate seamlessly with motorized shades for complete home automation.',
    website: 'https://control4.com',
    features: [
      'Complete smart home automation platform',
      'Native shade control with scheduling',
      'Scene creation for one-touch ambiance',
      'Voice control through Alexa and Google',
      'Energy management and reporting',
      'Professional installation and support'
    ],
    products: [
      { name: 'EA Controllers', description: 'Brain of the smart home' },
      { name: 'Touch Screens', description: 'Wall and tabletop interfaces' },
      { name: 'Keypads', description: 'Designer control switches' },
      { name: 'App Control', description: 'iOS and Android apps' }
    ],
    stats: [
      { value: '500K+', label: 'Homes Automated' },
      { value: '#1', label: 'Smart Home Platform' },
      { value: '24/7', label: 'Support' }
    ]
  },
  'lutron-homeworks': {
    relationship: 'partner',
    name: 'Lutron HomeWorks',
    tagline: 'The Gold Standard',
    logo: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/ylx0vipe_image.png',
    heroImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&h=800&fit=crop',
    description: 'Lutron HomeWorks is the premier whole-home lighting and shade control system. Chosen for the world\'s most prestigious residences, HomeWorks offers unparalleled reliability, precision, and customization.',
    website: 'https://lutron.com/homeworks',
    features: [
      'Total light and shade control solution',
      'Precision fade dimming technology',
      'Designer keypad and switch options',
      'Integration with all major AV systems',
      'Hyperion solar adaptive shading',
      'Professional programming and support'
    ],
    products: [
      { name: 'HomeWorks QSX', description: 'Latest generation processor' },
      { name: 'Palladiom', description: 'Architectural keypads' },
      { name: 'Sivoia QS', description: 'Precision shade systems' },
      { name: 'Ketra', description: 'Tunable natural light' }
    ],
    stats: [
      { value: '#1', label: 'Luxury Standard' },
      { value: 'Whole-home', label: 'Light & Shade Control' },
      { value: 'NYC', label: 'Installed by Apollo' }
    ]
  }
};

export const PartnerDetailPage = () => {
  const { partnerId } = useParams();
  const partner = PARTNER_DATA[partnerId];

  if (!partner) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-serif text-neutral-900 mb-4">Partner Not Found</h1>
            <Link to="/partners" className="text-amber-600 hover:text-amber-700">
              Return to Partners
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-white">
        {/* Hero Section */}
        <section className="relative min-h-[500px] md:min-h-[60vh] flex items-center pt-28 pb-12 md:pt-32 md:pb-16">
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-900 via-neutral-900/95 to-neutral-900/80">
          <img 
            src={partner.heroImage} 
            alt={partner.name}
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        
        <div className="container-xl relative z-10">
          <BackButton variant="light" />
          <Breadcrumbs light className="mt-6" />
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mt-8"
          >
            {/* Partner Logo */}
            {partner.logo && (
              <div className="mb-6">
                <img 
                  src={partner.logo} 
                  alt={`${partner.name} logo`}
                  className="h-16 max-w-[200px] object-contain bg-white/10 backdrop-blur-sm rounded-lg p-3"
                />
              </div>
            )}
            <span className="inline-block px-4 py-2 bg-amber-500/20 text-amber-400 text-sm font-medium rounded-full mb-6" data-testid="partner-relationship-badge">
              {RELATIONSHIP_COPY[partner.relationship].badge}
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif text-white mb-4 break-words">
              {partner.name}
            </h1>
            <p className="text-2xl text-amber-400 mb-6">{partner.tagline}</p>
            <p className="text-lg text-white/70 leading-relaxed mb-8">
              {partner.description}
            </p>
            <a 
              href={partner.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white border border-white/30 px-6 py-3 rounded-full hover:bg-white/10 transition-colors"
            >
              <Globe className="w-5 h-5" />
              Visit Website
              <ExternalLink className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-neutral-900">
        <div className="container-xl">
          <div className="grid grid-cols-3 gap-3 sm:gap-8 max-w-3xl mx-auto">
            {partner.stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-2">
                  {stat.value}
                </div>
                <div className="text-white/60 text-xs sm:text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 overflow-x-clip">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-serif text-neutral-900 mb-6">
                {RELATIONSHIP_COPY[partner.relationship].heading(partner.name)}
              </h2>
              <p className="text-lg text-neutral-600 mb-8" data-testid="partner-relationship-text">
                {RELATIONSHIP_COPY[partner.relationship].text(partner.name)}
              </p>
              
              <div className="space-y-4">
                {partner.features.map((feature, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center mt-0.5">
                      <Check className="w-4 h-4 text-amber-600" />
                    </div>
                    <span className="text-neutral-700">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0"
            >
              {partner.products.map((product, idx) => (
                <div
                  key={idx}
                  className="min-w-0 bg-neutral-50 rounded-xl p-5 sm:p-6 hover:bg-amber-50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center mb-4">
                    <Award className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-semibold text-neutral-900 mb-2">{product.name}</h3>
                  <p className="text-sm text-neutral-600">{product.description}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-amber-50 to-amber-100">
        <div className="container-xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <Zap className="w-12 h-12 text-amber-600 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-serif text-neutral-900 mb-6">
              Experience {partner.name} Products
            </h2>
            <p className="text-lg text-neutral-600 mb-8">
              Discover how {partner.name} technology can transform your space. 
              Schedule a consultation with our experts.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/consultation" 
                className="btn-primary px-8 py-4 text-lg"
              >
                Schedule Consultation
              </Link>
              <Link 
                to="/products" 
                className="btn-outline px-8 py-4 text-lg"
              >
                Browse Products
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </Layout>
  );
};

export default PartnerDetailPage;
